import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { THEME_MAP, useUITheme } from "@/app/hooks/ui_hooks/useUITheme";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { setSidebarState } from "@/app_redux/reducers/slices/ui_states_slice";
import clsx from "clsx";
import { MdOutlineInventory } from "react-icons/md";
import { BiTransfer } from "react-icons/bi";
import { IoDocumentTextOutline, IoFlaskOutline } from "react-icons/io5";
import {
  CalendarDaysIcon,
  ClockIcon,
  Cog6ToothIcon,
  ShoppingBagIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { PiUserListLight } from "react-icons/pi";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Header from "../../core/Header";
import StaticSidebarComponent from "../../core/StaticSidebar";
import RootContainerModals from "../../templates/RootContainerModals";
import RootContainerSlideovers from "../../templates/RootContainerSlideovers";
import ToastNotificationComponent from "../../templates/ToastNotificationComponent";
import { IUIContainerProps } from "./interface";
import RBACFlag from "../../core/RBACFlag";
import homeServiceIcon from "@assets/images/homeservice.svg";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RouteKeys } from "@/api_framework/api_modals/Routers";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState, setExotelInformation } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { LuListTodo } from "react-icons/lu";
import { toast, useToaster } from "react-hot-toast";
import StickyPanel from "./StickyPanel";
import VeticExotelIntegration from "../../routes/auth_routes/VeticExotelIntegration";
import { IExotelInformationData } from "@/api_framework/api_modals/Users";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useExotelContextEngineAPI } from "@/app/hooks/api_hooks/ameyo/useExotelContextEngineAPI";
import { BLUE7_BORIVALI_CLINIC_ID } from "@/constants";
import { useLabReportInterpretationAlertStreamAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationAlertStreamAPI";

const HomeServicesIcon = ({ className }: { className?: string }) => (
  <img src={homeServiceIcon} className={className} alt="Home Services" />
);

const UIContainer: React.FC<React.PropsWithChildren<IUIContainerProps>> = (props) => {
  const { getRouteKey } = useRouter();
  const location = useLocation(); // Get the current route path
  const { theme } = useUITheme();
  const {
    env: { version },
  } = useConfiguration();
  const { VETIC_CHAR_LOGO_WHITE } = useImageConstants();
  const { desktop, mobile } = useAppSelector((state) => state.root.UIStates.sidebar);
  // RBAC: UI_CLINIC_NUDGE_NOTIFICATION — legacy roles: clinic_ops, clinic_ops_doggyworld
  const showClinicNudge = useFeatureAccess("UI_CLINIC_NUDGE_NOTIFICATION");
  // Blue7Vets, Borivali: clinic toast/nudge notification is disabled for this clinic.
  const mappedClinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const isNudgeDisabledForClinic = mappedClinicId === BLUE7_BORIVALI_CLINIC_ID;
  const authUser = useAppSelector((state) => state?.auth?.user);
  const isSuperAdmin = useAppSelector((state) => state.auth?.userPermissions?.role_name) === "super_admin";
  useLabReportInterpretationAlertStreamAPI(useFeatureAccess("LAB_REPORT_INTERPRETATION_LIST") && !isSuperAdmin);
  const { toasts, handlers: toastHandlers } = useToaster();
  const visibleToastCount = toasts.filter((t) => t.visible).length;
  const prevVisibleToastCount = useRef(visibleToastCount);
  useEffect(() => {
    if (visibleToastCount < prevVisibleToastCount.current) toastHandlers.endPause();
    prevVisibleToastCount.current = visibleToastCount;
  }, [visibleToastCount, toastHandlers]);
  const { execute: logClickAPI } = useClickLogAPI();
  const { execute: fetchContextEngineData, contextEngineData } = useExotelContextEngineAPI();
  const lastLoggedPanelKeyRef = useRef<string>("");
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { petDetails, customerId, petId, exotelInformation } = routeState.state as ICustomerProfileState;
  const fetchCities = useOrgCitiesListAPI("PATIENT_WAITING_LIST");

  const dispatch = useAppDispatch();
  const [isExotelPanelDismissed, setIsExotelPanelDismissed] = useState(false);

  const toggleSidebar = useCallback(
    (_status: boolean) => {
      dispatch(
        setSidebarState({
          mobile: { ...mobile, show: false },
          desktop: { ...desktop, show: !desktop.show },
        }),
      );
    },
    [dispatch, mobile, desktop],
  );

  useEffect(() => {
    const formatLastRouteSegment = (route: string): string => {
      const segments = route?.split("/")?.filter(Boolean);
      if (segments?.some((segment) => ["user-cart", "customer-details", "slots"].includes(segment))) {
        return petDetails ? petDetails?.name?.toUpperCase() : "No Pet Name";
      }
      if (!route || typeof route !== "string") return "";

      const lastSegment = route.split("/").filter(Boolean).pop();
      if (!lastSegment) return "";
      return lastSegment.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    };

    document.title = `${formatLastRouteSegment(location.pathname)}`;
  }, [location.pathname, petDetails]);

  // Sticky panel drag handlers are handled inside StickyPanel.

  //For all nudges
  const pathSegments = useMemo(() => location.pathname.split("/").filter(Boolean), [location.pathname]);
  const isMirrorViewEnable = useMemo(() => pathSegments.includes("mirror-view"), [pathSegments]);

  useEffect(() => {
    if (!pathSegments.includes("cart-creation")) {
      localStorage.setItem("nudgesShown_cart", "false");
      localStorage.removeItem("Mirrorcartid");
    }

    if (!pathSegments.includes("customer-create-emr")) {
      localStorage.setItem("nudgesShown_emr", "false");
    }

    const bookingPaths = ["available-slots", "booked-slots", "overbooking"];
    const isOnBookingRoute = bookingPaths.some((val) => pathSegments.includes(val));

    if (!isOnBookingRoute) {
      localStorage.setItem("nudgesShown_bookings", "false");
    }
  }, [pathSegments]);

  // Sticky panel actions are handled inside StickyPanel.

  const navigation = [
    {
      name: "Customer Search",
      href: getRouteKey("SEARCH_CUSTOMER", "url"),
      defaultHref: getRouteKey("SEARCH_CUSTOMER", "url"),
      icon: UserCircleIcon,
      current: false,
    },
    {
      name: "Appointments",
      href: getRouteKey("CHECK_SLOTS", "url"),
      defaultHref: getRouteKey("CHECK_SLOTS", "url"),
      icon: ClockIcon,
      current: false,
    },
    {
      name: "Patient Waiting List",
      href: getRouteKey("PATIENT_WAITING_LIST", "url"),
      defaultHref: getRouteKey("PATIENT_WAITING_LIST", "url"),
      icon: PiUserListLight,
      current: false,
    },
    {
      name: "At Home Services",
      href: getRouteKey("AT_HOME_SERVICES", "url"),
      defaultHref: getRouteKey("AT_HOME_SERVICES", "url"),
      icon: HomeServicesIcon,
      current: false,
    },
    {
      name: "Staff Calendar",
      defaultHref: getRouteKey("STAFF_CALENDAR", "url"),
      href: getRouteKey("STAFF_CALENDAR", "url"),
      icon: CalendarDaysIcon,
      current: false,
    },
    {
      name: "Order Management",
      defaultHref: getRouteKey("ORDER_MANAGEMENT", "url"),
      href: getRouteKey("ORDER_MANAGEMENT", "url"),
      icon: ShoppingBagIcon,
      current: false,
    },
    {
      name: "Accounting",
      defaultHref: getRouteKey("REFUND_REQUEST", "url"),
      href: getRouteKey("ACCOUNTING", "url"),
      icon: BiTransfer,
      current: false,
    },
    {
      name: "Day Closing",
      defaultHref: getRouteKey("CLINIC_CLOSING", "url"),
      href: getRouteKey("CLINIC_CLOSING", "url"),
      icon: LuListTodo,
      current: false,
    },
    {
      name: "Lab Report",
      defaultHref: getRouteKey("LAB_REPORT", "url"),
      href: getRouteKey("LAB_REPORT", "url"),
      icon: IoFlaskOutline,
      current: false,
    },
    {
      name: "Lab Report Interpretation",
      defaultHref: getRouteKey("LAB_REPORT_INTERPRETATION_LIST", "url"),
      href: getRouteKey("LAB_REPORT_INTERPRETATION_LIST", "url"),
      icon: IoDocumentTextOutline,
      current: false,
      actionId: "LAB_REPORT_INTERPRETATION_LIST" as RouteKeys,
    },
    {
      name: "Inventory",
      defaultHref: getRouteKey("PURCHASE_LIST", "url"),
      href: "#",
      icon: MdOutlineInventory,
      current: false,
    },
  ];

  const isShowNudge = () => {
    const route = location.pathname;
    const segments = route.split("/").filter(Boolean); // removes empty strings from split
    if (segments.includes("cart-creation")) {
      return false;
    }
    return true;
  };

  const disconnectToastIdRef = useRef<string | null>(null);
  const hasEverConnectedRef = useRef(false);
  useEffect(() => {
    const checkConnectionHealth = () => {
      if (!navigator.onLine) {
        if (!disconnectToastIdRef.current && !isMirrorViewEnable) {
          disconnectToastIdRef.current = toast.error("Screen disconnected. Reconnecting. Please wait.", {
            duration: 10000,
          });
        }
        return;
      }
      if (disconnectToastIdRef.current) {
        toast.dismiss(disconnectToastIdRef.current);
        disconnectToastIdRef.current = null;
      }
      if (!hasEverConnectedRef.current) return;
    };

    checkConnectionHealth();

    const interval = setInterval(checkConnectionHealth, 2000);

    const handleOnline = checkConnectionHealth;
    const handleOffline = checkConnectionHealth;

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [isMirrorViewEnable]);

  const stickyTitle = props.stickyTitle ?? "Customer Preview";
  const stickyContent = props.stickyContent ?? <VeticExotelIntegration />;

  const queryParams = new URLSearchParams(location.search);
  const isExotelFlow = queryParams.get("isExotel") === "true";
  const isManualControlPanelFlow = queryParams.get("control_panel") === "true";
  const crtId = queryParams.get("CRT") ?? queryParams.get("CRT_ID") ?? "";
  const phoneNumberFromURL = queryParams.get("phone_number") ?? "";
  const exotelInfoInSession = useMemo(() => {
    try {
      const rawSessionExotelInfo = sessionStorage.getItem("exotel_information_current");
      if (!rawSessionExotelInfo) return null;
      const parsedSessionExotelInfo = JSON.parse(rawSessionExotelInfo) as IExotelInformationData;
      return parsedSessionExotelInfo;
    } catch {
      return null;
    }
  }, [location.key]);
  const hasActiveExotelSession = Boolean(exotelInfoInSession?.CRT);

  useEffect(() => {
    if (isExotelFlow || crtId || hasActiveExotelSession) {
      setIsExotelPanelDismissed(false);
    }
  }, [isExotelFlow, crtId, hasActiveExotelSession, location.key]);

  useEffect(() => {
    const handleExotelPanelClose = () => {
      setIsExotelPanelDismissed(true);
    };

    window.addEventListener("vetic:close-exotel-panel", handleExotelPanelClose);
    return () => {
      window.removeEventListener("vetic:close-exotel-panel", handleExotelPanelClose);
    };
  }, []);

  const shouldShowExotelPanel = (isExotelFlow || Boolean(crtId) || hasActiveExotelSession) && !isExotelPanelDismissed;

  useEffect(() => {
    if (!shouldShowExotelPanel) return;
    const resolvedCustomerId = customerId || exotelInformation?.customer_pet_data?.customer_id || exotelInfoInSession?.customer_pet_data?.customer_id || "";
    if (!resolvedCustomerId) return;
    const resolvedDidNumber = exotelInformation?.did_number || exotelInfoInSession?.did_number || null;
    fetchContextEngineData(resolvedCustomerId, resolvedDidNumber);
  }, [shouldShowExotelPanel, customerId, exotelInformation, exotelInfoInSession, fetchContextEngineData]);

  useEffect(() => {
    if (!shouldShowExotelPanel) { lastLoggedPanelKeyRef.current = ""; return; }

    const resolvedCrtId = crtId || exotelInformation?.CRT || exotelInfoInSession?.CRT || "";
    const resolvedPhone = phoneNumberFromURL || exotelInformation?.rawPhoneNumber || exotelInfoInSession?.rawPhoneNumber || "";
    const resolvedCustomerId = customerId || exotelInformation?.customer_pet_data?.customer_id || exotelInfoInSession?.customer_pet_data?.customer_id || "";
    // Talking points are only fetched when we have a customer id. For a known
    // customer, wait for that fetch so the log carries the talking points; for a
    // new/unknown caller no fetch is coming, so don't block on it — otherwise the
    // panel-open would never be logged for new callers.
    if (resolvedCustomerId && !contextEngineData) return;
    if (!resolvedPhone && !isManualControlPanelFlow) return;
    const panelKey = `${resolvedCrtId}|${resolvedPhone}`;
    if (lastLoggedPanelKeyRef.current === panelKey) return;
    lastLoggedPanelKeyRef.current = panelKey;

    const logBody: IClickLogBody = {
      event_type: "CONTROL_PANEL_OPEN",
      scr_id: "EXOTEL_PANEL",
      crt_id: resolvedCrtId,
      agent_id: authUser?.email ?? "",
      customer_type: resolvedCustomerId ? "existing" : "new",
      source: isManualControlPanelFlow ? "manual" : "auto",
      phone: resolvedPhone,
      opened_at: Date.now(),
      number_of_talking_points: contextEngineData?.signals?.length ?? 0,
      talking_points_data: contextEngineData ?? undefined,
    };
    logClickAPI(logBody);
  }, [shouldShowExotelPanel, contextEngineData, crtId, phoneNumberFromURL, customerId, exotelInformation, exotelInfoInSession, isManualControlPanelFlow, authUser, logClickAPI]);

  useEffect(() => {
    if (!isExotelFlow && !crtId) return;

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const appointmentSegmentIndex = pathSegments.findIndex((segment) => segment === "appointments");
    const appointmentParams =
      appointmentSegmentIndex >= 0 ? pathSegments.slice(appointmentSegmentIndex + 1, appointmentSegmentIndex + 3) : [];
    const customerIdFromPath = appointmentParams[0] ?? null;
    const petIdFromPath = appointmentParams[1] ?? null;

    const constructedBody: IExotelInformationData = {
      event: "incoming_call",
      customer_pet_data: {
        customer_id: customerId || customerIdFromPath || exotelInfoInSession?.customer_pet_data?.customer_id || null,
        pet_id: petId || petIdFromPath || exotelInfoInSession?.customer_pet_data?.pet_id || null,
      },
      rawPhoneNumber: phoneNumberFromURL || exotelInfoInSession?.rawPhoneNumber || null,
      CRT: crtId || exotelInfoInSession?.CRT || null,
    };

    dispatch(setExotelInformation(constructedBody));
    const storageKey = constructedBody.CRT ? `exotel_information:${constructedBody.CRT}` : "exotel_information";
    sessionStorage.setItem(storageKey, JSON.stringify(constructedBody));
    sessionStorage.setItem("exotel_information_current", JSON.stringify(constructedBody));
  }, [customerId, petId, crtId, dispatch, exotelInfoInSession, isExotelFlow, location.pathname, phoneNumberFromURL]);

  return (
    <div className={clsx(THEME_MAP[theme])}>
      <div className="bg-gray-50 min-h-screen">
        {/* Static sidebar for desktop */}
        {!isMirrorViewEnable && <StaticSidebarComponent toggleSidebar={toggleSidebar} />}

        {showClinicNudge && isShowNudge() && !isMirrorViewEnable && !isNudgeDisabledForClinic && <ToastNotificationComponent />}
        {!desktop.show && !isMirrorViewEnable && (
          <div className="hidden lg:block lg:left-0 lg:z-40 fixed lg:inset-y-0 lg:bg-gray-900 lg:pb-4 lg:w-20 overflow-visible">
            <div className="flex justify-center items-center h-16 shrink-0">
              <img
                className="w-auto  h-4  lg:w-4 lg:h-4 xl:w-6 xl:h-5 2xl:w-6 2xl:h-6"
                src={VETIC_CHAR_LOGO_WHITE}
                alt="Vetic Logo"
              />
            </div>
            <nav className="mt-2">
              <ul role="list" className="flex flex-col items-center ">
                {navigation.map((item) => {
                  const navLink = (
                    <Link to={item?.defaultHref}>
                      <li data-tooltip-id="nav_button" data-tooltip-content={item?.name} key={item.name}>
                        <button
                          // onClick={() => toggleSidebar(true)}
                          className={clsx(
                            item.current ? "bg-gray-800 text-white" : "text-gray-300 hover:text-white hover:bg-gray-800",
                            "group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold",
                          )}
                        >
                          <item.icon
                            className=" w-4 h-4  lg:w-4 lg:h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 shrink-0"
                            aria-hidden="true"
                          />
                          <span className="sr-only">{item.name}</span>
                        </button>
                      </li>
                    </Link>
                  );
                  return item.actionId ? (
                    <RBACFlag key={item.name} actionId={item.actionId}>
                      {navLink}
                    </RBACFlag>
                  ) : (
                    <Fragment key={item.name}>{navLink}</Fragment>
                  );
                })}
                <RBACFlag actionId="CONFIGURATION_MODULE">
                  <li className="sm:bottom-10 bottom-24 absolute ">
                    <Link
                      to={getRouteKey("CONFIGURATION", "url")}
                      className="flex flex-col gap-x-3 gap-y-4 hover:bg-gray-800 -mx-2 p-2 rounded-md font-semibold text-gray-400 text-sm hover:text-white leading-6 group"
                    >
                      <Cog6ToothIcon
                        className=" w-4 h-4  lg:w-4 lg:h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 shrink-0"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                </RBACFlag>
                {/* <li className="bottom-2 absolute ">
                  <span className="flex flex-col gap-x-3 gap-y-4 hover:bg-gray-800 -mx-2 p-2 rounded-md font-semibold text-gray-400 text-sm hover:text-white leading-6 group">
                    <span className="text-xs">v{version}</span>
                  </span>
                </li> */}
              </ul>
            </nav>
            <Tooltip id="nav_button" />
          </div>
        )}

        <div
          className={
            isMirrorViewEnable ? null : desktop?.show ? "pl-0 md:pl-60 lg:pl-60 xl:pl-60 2xl:pl-72" : "lg:pl-20"
          }
        >
          <Header toggleSidebar={toggleSidebar} isMirrorViewEnable={isMirrorViewEnable} />

          <main>
            <div className="relative px-2 sm:px-2 lg:px-3 bg-white">{props?.children}</div>
          </main>
        </div>
      </div>
      {shouldShowExotelPanel && (
        <StickyPanel
          // Remount on each new call/Control-Panel click (fresh CRT → fresh key)
          // so the panel always reopens, even after the user closed it locally.
          key={crtId || exotelInfoInSession?.CRT || "sticky-panel"}
          title={stickyTitle}
          content={stickyContent}
          defaultOpen={(props.stickyDefaultOpen ?? true) || shouldShowExotelPanel}
          forceOpen={shouldShowExotelPanel}
          dockRight
          showCloseButton={isManualControlPanelFlow}
        />
      )}
      <RootContainerModals />
      <RootContainerSlideovers />
    </div>
  );
};

export default UIContainer;
